import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { PendingScrape } from '@/models/pending-scrape.model';
import { ScrapedRestaurant, ScrapingService } from './scraping.service';
import { SalesService } from './sales.service';

export class PendingScrapeService {
  /**
   * Create pending scrape records
   */
  static async createPendingScrapes(
    restaurants: ScrapedRestaurant[],
    userId: string
  ): Promise<string[]> {
    const batch = writeBatch(db);
    const ids: string[] = [];

    restaurants.forEach((restaurant) => {
      const docRef = doc(collection(db, 'pendingScrapes'));
      ids.push(docRef.id);
      batch.set(docRef, {
        scrapedData: restaurant,
        status: 'pending',
        scrapedBy: userId,
        scrapedAt: Timestamp.now(),
      });
    });

    await batch.commit();
    return ids;
  }

  /**
   * Get pending scrapes
   */
  static async getPendingScrapes(): Promise<PendingScrape[]> {
    const q = query(
      collection(db, 'pendingScrapes'),
      where('status', '==', 'pending'),
      orderBy('scrapedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      scrapedData: doc.data().scrapedData,
      scrapedAt: doc.data().scrapedAt?.toDate() || new Date(),
      reviewedAt: doc.data().reviewedAt?.toDate(),
    })) as PendingScrape[];
  }

  /**
   * Get all scrapes (pending, approved, rejected)
   */
  static async getAllScrapes(): Promise<PendingScrape[]> {
    const q = query(
      collection(db, 'pendingScrapes'),
      orderBy('scrapedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      scrapedData: doc.data().scrapedData,
      scrapedAt: doc.data().scrapedAt?.toDate() || new Date(),
      reviewedAt: doc.data().reviewedAt?.toDate(),
    })) as PendingScrape[];
  }

  /**
   * Get single pending scrape
   */
  static async getPendingScrape(id: string): Promise<PendingScrape> {
    const docRef = doc(db, 'pendingScrapes', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Pending scrape not found');
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      scrapedData: data.scrapedData,
      scrapedAt: data.scrapedAt?.toDate() || new Date(),
      reviewedAt: data.reviewedAt?.toDate(),
    } as PendingScrape;
  }

  /**
   * Approve pending scrape and convert to lead
   */
  static async approvePendingScrape(
    id: string,
    userId: string,
    assignedTo?: string
  ): Promise<string> {
    const pendingScrape = await this.getPendingScrape(id);
    
    if (pendingScrape.status !== 'pending') {
      throw new Error('Scrape is not pending');
    }

    // Convert to lead
    const lead = ScrapingService.convertToLead(
      pendingScrape.scrapedData,
      assignedTo || userId
    );

    const leadId = await SalesService.createLead(lead);

    // Update pending scrape status
    await updateDoc(doc(db, 'pendingScrapes', id), {
      status: 'approved',
      reviewedBy: userId,
      reviewedAt: Timestamp.now(),
    });

    return leadId;
  }

  /**
   * Approve multiple pending scrapes
   */
  static async approvePendingScrapes(
    ids: string[],
    userId: string,
    assignedTo?: string
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const id of ids) {
      try {
        await this.approvePendingScrape(id, userId, assignedTo);
        success++;
      } catch (error: any) {
        failed++;
        errors.push(`${id}: ${error.message}`);
      }
    }

    return { success, failed, errors };
  }

  /**
   * Reject pending scrape
   */
  static async rejectPendingScrape(
    id: string,
    userId: string,
    reason?: string
  ): Promise<void> {
    await updateDoc(doc(db, 'pendingScrapes', id), {
      status: 'rejected',
      reviewedBy: userId,
      reviewedAt: Timestamp.now(),
      rejectionReason: reason,
    });
  }

  /**
   * Delete pending scrape
   */
  static async deletePendingScrape(id: string): Promise<void> {
    await deleteDoc(doc(db, 'pendingScrapes', id));
  }
}

